import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert
} from "@mui/material";
import { useContents } from "../../hooks/useContents";
import ContentTable from "../../components/ContentTable";
import AddContentForm from "../../components/AddContentForm";
import { usePageContext } from "../../context/PageContext";
import { useAuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { getApiErrorDisplayMessage } from "../../utils/channelSaveError";

const Contents = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const { userData } = useAuthContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedContent, setSelectedContent] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, error, refetch, updateMutation, deleteMutation, isLoading } =
    useContents(currentPage, pageSize, search);

  useEffect(() => {
    if (error) setErrorMessage(t("contentsPage.loadError"));
    else setErrorMessage(null);
  }, [error, t]);

  const handleEditContent = (c) => {
    setSelectedContent(c);
    setErrorMessage(null);
  };
  const handleDeleteContent = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };
  const confirmDeleteContent = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => { setConfirmDelete(null); refetch(); },
      onError: (err) =>
        setErrorMessage(getApiErrorDisplayMessage(err, "contentsPage.deleteError")),
      onSettled: () => setIsProcessing(false),
    });
  };

  // Toggle status
  const toggleStatus = (c) => {
    setIsProcessing(true);
    updateMutation.mutate(
      { id: c.id, status: c.status ? 0 : 1, id_usuario_ultima_alteracao: userData?.id ?? null },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(getApiErrorDisplayMessage(err, "contentsPage.statusError")),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  // Ações em massa
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(getApiErrorDisplayMessage(err, "contentsPage.activateError"));
    } finally { setIsProcessing(false); }
  };
  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(getApiErrorDisplayMessage(err, "contentsPage.deactivateError"));
    } finally { setIsProcessing(false); }
  };
  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(getApiErrorDisplayMessage(err, "contentsPage.bulkDeleteError"));
    } finally { setIsProcessing(false); }
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => { setSearch(term); refetch(); };
  const handleCloseForm = () => { setSelectedContent(null); setErrorMessage(null); };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("contentsPage.heading")}</Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedContent({})}
        >
          {t("contentsPage.addContentButton")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="error"
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
          >
            <Typography
              variant="body2"
              component="div"
              sx={{ whiteSpace: "pre-line" }}
            >
              {errorMessage}
            </Typography>
          </Alert>
        </Box>
      )}

      {selectedContent ? (
        <AddContentForm
          contentData={selectedContent}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={async (updated) => {
            setIsProcessing(true);
            setErrorMessage(null);
            try {
              await updateMutation.mutateAsync(updated);
              await refetch();
              handleCloseForm();
            } finally {
              setIsProcessing(false);
            }
          }}
        />
      ) : (
        <ContentTable
          contents={data?.contents || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditContent={handleEditContent}
          onDeleteContent={handleDeleteContent}
          onToggleStatus={toggleStatus}
          onBulkActivate={bulkActivate}
          onBulkDeactivate={bulkDeactivate}
          onBulkDelete={bulkDelete}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("contentsPage.deleteDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("contentsPage.processingDelete") : t("contentsPage.deleteConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteContent}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contents;
